import { createAsyncThunk } from '@reduxjs/toolkit';

import type { RootState } from 'features/app/store/store';
import {
  SIP_USER_AGENT_RECOVERY_POLL_INTERVAL_MS,
  SIP_USER_AGENT_RECOVERY_TIMEOUT_MS,
} from 'features/sip/constants';
import { SipSessionEventBus } from 'features/sip/services';

import { RegistrationStatus } from './sipConnectionSlice';
import {
  selectRegistrationStatus,
  selectRegistrationWanted,
} from './sipConnectionSelectors';

export type EnsureSipUserAgentOutcome =
  'registered' | 'failed' | 'not-wanted' | 'timeout';

export interface EnsureSipUserAgentResult {
  outcome: EnsureSipUserAgentOutcome;
  registrationStatus: RegistrationStatus;
}

const readOutcome = (state: RootState): EnsureSipUserAgentOutcome | null => {
  if (!selectRegistrationWanted(state)) {
    return 'not-wanted';
  }

  const registrationStatus = selectRegistrationStatus(state);

  if (registrationStatus === RegistrationStatus.Registered) {
    return 'registered';
  }

  if (registrationStatus === RegistrationStatus.Failed) {
    return 'failed';
  }

  return null;
};

/**
 * Asks `sip` to make sure a registered UA exists while registration is still
 * wanted, and waits for the result. Never writes the registration intent:
 * that belongs to `session`. Never rejects.
 */
export const ensureSipUserAgentRegistered = createAsyncThunk<
  EnsureSipUserAgentResult,
  void,
  { state: RootState }
>('sip/ensureUserAgentRegistered', async (_, { getState }) => {
  const immediateOutcome = readOutcome(getState());

  if (immediateOutcome) {
    return {
      outcome: immediateOutcome,
      registrationStatus: selectRegistrationStatus(getState()),
    };
  }

  SipSessionEventBus.userAgentStartRequested$.next();

  const deadline = Date.now() + SIP_USER_AGENT_RECOVERY_TIMEOUT_MS;

  return new Promise<EnsureSipUserAgentResult>((resolve) => {
    const intervalId = setInterval(() => {
      const state = getState();
      const outcome =
        readOutcome(state) ?? (Date.now() >= deadline ? 'timeout' : null);

      if (outcome) {
        clearInterval(intervalId);
        resolve({
          outcome,
          registrationStatus: selectRegistrationStatus(state),
        });
      }
    }, SIP_USER_AGENT_RECOVERY_POLL_INTERVAL_MS);
  });
});
